import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Hook for paginated list endpoints that return X-Total-Count header.
 *
 * Usage:
 *   const { items, total, pageSize, page, setPage, setPageSize, loading, reload } =
 *     usePaginatedList("/estimates", { status: "Draft" });
 *
 * - Initial page size: 200 (overridable via `initialPageSize`).
 * - Allowed page sizes: 200 / 500 (toggle via `setPageSize`).
 * - When total > pageSize → caller renders pagination controls (PaginationControls component).
 * - `params` should be a stable object — pass through via useMemo if deriving.
 */
export function usePaginatedList(url, params = {}, initialPageSize = 200, options = {}) {
  const { enabled = true } = options;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Stable signature for the params dependency
  const sig = JSON.stringify(params || {});

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError("");
    try {
      const skip = (page - 1) * pageSize;
      const merged = { ...params, skip, limit: pageSize };
      // Drop empty/null/undefined keys so they don't show up in the URL
      Object.keys(merged).forEach((k) => {
        if (merged[k] === "" || merged[k] === null || merged[k] === undefined) {
          delete merged[k];
        }
      });
      const res = await api.get(url, { params: merged });
      const data = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const totalHeader = res.headers?.["x-total-count"] ?? res.headers?.["X-Total-Count"];
      const totalNum = totalHeader != null ? Number(totalHeader) : data.length;
      setItems(data);
      setTotal(Number.isFinite(totalNum) ? totalNum : data.length);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, sig, page, pageSize, enabled]);

  // Auto reload on page/size/url/params change
  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever params (filters) or pageSize change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, pageSize]);

  const handleSetPageSize = (n) => {
    setPageSize(Number(n) || 200);
  };

  return {
    items,
    total,
    page,
    pageSize,
    pageCount: Math.max(1, Math.ceil(total / pageSize)),
    loading,
    error,
    setPage,
    setPageSize: handleSetPageSize,
    reload: load,
  };
}
